// Sterile Mate — AMMEX Disposable Gloves catalog (PUBLIC-SAFE)
// -----------------------------------------------------------------------------
// SECURITY: This file ships in the frontend JS bundle. It MUST NOT contain
// AMMEX cost pricing. Only retail pricing + specs live here. Admins fetch
// cost figures at runtime from the authenticated backend endpoint
//   GET /api/gloves/cost-pricing
// which enforces role super_admin / hub_admin.
//
// The retail figures below are pre-computed (cost × 1.44) and hard-coded so
// no reverse-engineering of the markup + retail can reveal cost prices from
// this file alone.
// -----------------------------------------------------------------------------

// UOM (Unit of Measure) legend used across the marketplace:
//   PC = Piece  (an individual single unit)
//   PR = Pair   (a set of two matching items — surgical gloves, shoe covers)
//   BX = Box    (a small dispenser box of pieces)
//   CA = Case   (a full case — typically 10 boxes / 1,000 pieces)
export const UOM_LABELS = {
  PC: { code: "PC", label: "Piece", desc: "An individual single unit" },
  PR: { code: "PR", label: "Pair", desc: "A set of two matching items" },
  BX: { code: "BX", label: "Box", desc: "A dispenser box of pieces" },
  CA: { code: "CA", label: "Case", desc: "A full case (typically 10 boxes)" },
};

export const GLOVE_MATERIALS = [
  { key: "all", label: "All Gloves", accent: "bg-slate-900" },
  { key: "nitrile", label: "Nitrile", accent: "bg-blue-600" },
  { key: "latex", label: "Latex", accent: "bg-amber-600" },
  { key: "vinyl", label: "Vinyl", accent: "bg-emerald-600" },
  { key: "poly", label: "Poly / General Purpose", accent: "bg-slate-500" },
];

// Standard exam-glove size table used across most AMMEX lines.
const EXAM_SIZES_S_XL = (prefix) => [
  { size: "S", sku: `${prefix}42100` },
  { size: "M", sku: `${prefix}44100` },
  { size: "L", sku: `${prefix}46100` },
  { size: "XL", sku: `${prefix}48100` },
];

export const GLOVES = [
  // =========================================================================
  // AMMEX PROFESSIONAL — EXAM (medical-grade)
  // =========================================================================
  {
    id: "ammex-pro-black-nitrile-exam",
    name: "AMMEX Professional Black Nitrile PF Exam Gloves",
    brand: "AMMEX Professional",
    material: "nitrile",
    color: "Black",
    mil: 3,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Medical exam · law enforcement · dental · lab",
    features: [
      "FDA 510(k) medical-exam cleared",
      "Chemo tested (ASTM D6978)",
      "Fentanyl resistant",
      "Latex-free · powder-free",
      "Textured fingertips",
    ],
    standards: ["FDA 510(k)", "ASTM D6319", "ASTM D6978", "EN 455-1/2/3/4"],
    image: "/api/static/gloves/real/GPNB-black-nitrile.png",
    productUrl: "https://www.ammexprofessional.com/product/abnpf-black-nitrile-exam-gloves",
    specSheet: "https://www.ammexprofessional.com/sites/default/files/ABNPF-spec.pdf",
    retailPerCase: 85.68,
    retailPerPiece: 0.0857,
    variants: EXAM_SIZES_S_XL("ABNPF"),
  },
  {
    id: "ammex-pro-cerulean-nitrile-exam",
    name: "AMMEX Professional Cerulean Nitrile PF Exam Gloves",
    brand: "AMMEX Professional",
    material: "nitrile",
    color: "Cerulean Blue",
    mil: 3.5,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Medical exam · clinical · outpatient",
    features: [
      "FDA 510(k) exam-grade",
      "Chemo tested (ASTM D6978)",
      "Latex-free · powder-free",
      "Enhanced tactile sensitivity",
    ],
    standards: ["FDA 510(k)", "ASTM D6319", "ASTM D6978"],
    image: "/api/static/gloves/real/X3-blue-nitrile.png",
    productUrl: "https://www.ammexprofessional.com/product/acnpf-cerulean-nitrile-exam-gloves",
    specSheet: "https://www.ammexprofessional.com/sites/default/files/ACNPF-spec.pdf",
    retailPerCase: 100.80,
    retailPerPiece: 0.1008,
    variants: EXAM_SIZES_S_XL("ACNPF"),
  },
  {
    id: "ammex-pro-indigo-nitrile-exam",
    name: "AMMEX Professional Indigo Nitrile PF Exam Gloves",
    brand: "AMMEX Professional",
    material: "nitrile",
    color: "Indigo",
    mil: 3,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Medical exam · dental · lab",
    features: [
      "FDA 510(k) exam-grade",
      "Chemo tested",
      "Latex-free · powder-free",
      "Textured fingertips",
    ],
    standards: ["FDA 510(k)", "ASTM D6319", "ASTM D6978"],
    image: "/api/static/gloves/real/GWRBN-royal-blue-nitrile.png",
    productUrl: "https://www.ammexprofessional.com/product/ainpf-indigo-nitrile-exam-gloves",
    specSheet: "https://www.ammexprofessional.com/sites/default/files/AINPF-spec.pdf",
    retailPerCase: 85.68,
    retailPerPiece: 0.0857,
    variants: EXAM_SIZES_S_XL("AINPF"),
  },
  {
    id: "ammex-pro-blue-nitrile-exam",
    name: "AMMEX Professional Blue Nitrile PF Exam Gloves",
    brand: "AMMEX Professional",
    material: "nitrile",
    color: "Blue",
    mil: 3,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Medical exam · everyday clinical workhorse",
    features: [
      "FDA 510(k) exam-grade",
      "Chemo tested (ASTM D6978)",
      "Fentanyl resistant",
      "Latex-free · powder-free",
    ],
    standards: ["FDA 510(k)", "ASTM D6319", "ASTM D6978", "EN 455"],
    image: "/api/static/gloves/real/X3-blue-nitrile.png",
    productUrl: "https://www.ammexprofessional.com/product/apfn-blue-nitrile-exam-gloves",
    specSheet: "https://www.ammexprofessional.com/sites/default/files/APFN-spec.pdf",
    retailPerCase: 85.68,
    retailPerPiece: 0.0857,
    variants: [
      { size: "XS", sku: "APFN40100" },
      { size: "S", sku: "APFN42100" },
      { size: "M", sku: "APFN44100" },
      { size: "L", sku: "APFN46100" },
      { size: "XL", sku: "APFN48100" },
    ],
  },
  {
    id: "ammex-pro-ivory-latex-exam",
    name: "AMMEX Professional Ivory Latex PF Exam Gloves",
    brand: "AMMEX Professional",
    material: "latex",
    color: "Ivory",
    mil: 5,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Medical exam · traditional clinical feel",
    features: [
      "FDA 510(k) exam-grade",
      "Powder-free",
      "Textured fingertips",
      "Excellent tactile sensitivity",
    ],
    standards: ["FDA 510(k)", "ASTM D3578", "EN 455"],
    image: "/api/static/gloves/real/TLF-ivory-latex.png",
    productUrl: "https://www.ammexprofessional.com/product/gppft-ivory-latex-exam-gloves",
    specSheet: "https://www.ammexprofessional.com/sites/default/files/GPPFT-spec.pdf",
    retailPerCase: 92.88,
    retailPerPiece: 0.0929,
    variants: EXAM_SIZES_S_XL("GPPFT"),
  },

  // =========================================================================
  // GLOVEWORKS — INDUSTRIAL (heavier duty, non-medical)
  // =========================================================================
  {
    id: "gloveworks-black-nitrile-exam",
    name: "Gloveworks Black Nitrile PF Exam Gloves",
    brand: "Gloveworks",
    material: "nitrile",
    color: "Black",
    mil: 5,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "EMS · first-responder · law enforcement · tattoo",
    features: [
      "FDA 510(k) exam-grade",
      "Chemo & fentanyl tested",
      "Heavy 5 mil thickness",
      "Textured fingertips",
      "Latex-free · powder-free",
    ],
    standards: ["FDA 510(k)", "ASTM D6319", "ASTM D6978"],
    image: "/api/static/gloves/real/GPNB-black-nitrile.png",
    productUrl: "https://www.gloveworks.com/product/gwben-black-nitrile-exam-gloves",
    specSheet: "https://www.gloveworks.com/sites/default/files/GWBEN-spec.pdf",
    retailPerCase: 151.20,
    retailPerPiece: 0.1512,
    variants: [
      { size: "S", sku: "GWBEN42100" },
      { size: "M", sku: "GWBEN44100" },
      { size: "L", sku: "GWBEN46100" },
      { size: "XL", sku: "GWBEN48100" },
      { size: "XXL", sku: "GWBEN49100" },
    ],
  },
  {
    id: "gloveworks-black-nitrile-ind",
    name: "Gloveworks Black Nitrile PF Industrial Gloves",
    brand: "Gloveworks",
    material: "nitrile",
    color: "Black",
    mil: 6,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Automotive · industrial · janitorial",
    features: [
      "Industrial-grade 6 mil",
      "Diamond textured grip",
      "Chemical-resistant",
      "Latex-free · powder-free",
    ],
    standards: ["ASTM D6319", "EN 374"],
    image: "/api/static/gloves/real/GPNB-black-nitrile.png",
    productUrl: "https://www.gloveworks.com/product/gpnb-black-nitrile-industrial",
    specSheet: "https://www.gloveworks.com/sites/default/files/GPNB-spec.pdf",
    retailPerCase: 129.60,
    retailPerPiece: 0.1296,
    variants: [
      { size: "S", sku: "GPNB40100" },
      { size: "M", sku: "GPNB42100" },
      { size: "L", sku: "GPNB44100" },
      { size: "XL", sku: "GPNB46100" },
      { size: "XXL", sku: "GPNB48100" },
      { size: "XXXL", sku: "GPNB49100" },
    ],
  },
  {
    id: "gloveworks-blue-nitrile-ind",
    name: "Gloveworks Blue Nitrile PF Industrial Gloves",
    brand: "Gloveworks",
    material: "nitrile",
    color: "Blue",
    mil: 5,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Automotive · janitorial · food processing",
    features: [
      "Industrial-grade 5 mil",
      "Textured for wet/dry grip",
      "Latex-free · powder-free",
    ],
    standards: ["ASTM D6319"],
    image: "/api/static/gloves/real/X3-blue-nitrile.png",
    productUrl: "https://www.gloveworks.com/product/inpf-blue-nitrile-industrial",
    specSheet: "https://www.gloveworks.com/sites/default/files/INPF-spec.pdf",
    retailPerCase: 129.60,
    retailPerPiece: 0.1296,
    variants: [
      { size: "S", sku: "INPF42100" },
      { size: "M", sku: "INPF44100" },
      { size: "L", sku: "INPF46100" },
      { size: "XL", sku: "INPF48100" },
      { size: "XXL", sku: "INPF49100" },
    ],
  },
  {
    id: "gloveworks-blue-nitrile-hd-exam",
    name: "Gloveworks Heavy-Duty Blue Nitrile PF Exam Gloves",
    brand: "Gloveworks",
    material: "nitrile",
    color: "Blue",
    mil: 6,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "High-risk clinical · lab · outbreak response",
    features: [
      "FDA 510(k) exam-grade",
      "Chemo & fentanyl tested",
      "Heavy-duty 6 mil thickness",
      "Textured for enhanced grip",
      "Latex-free · powder-free",
    ],
    standards: ["FDA 510(k)", "ASTM D6319", "ASTM D6978"],
    image: "/api/static/gloves/real/X3-blue-nitrile.png",
    productUrl: "https://www.gloveworks.com/product/gpnhd-heavy-duty-blue-nitrile-exam",
    specSheet: "https://www.gloveworks.com/sites/default/files/GPNHD-spec.pdf",
    retailPerCase: 146.16,
    retailPerPiece: 0.1462,
    variants: [
      { size: "M", sku: "GPNHD64100" },
      { size: "L", sku: "GPNHD66100" },
      { size: "XL", sku: "GPNHD68100" },
    ],
  },
  {
    id: "gloveworks-blue-latex-hd-exam",
    name: "Gloveworks Heavy-Duty Blue Latex PF Exam Gloves",
    brand: "Gloveworks",
    material: "latex",
    color: "Blue",
    mil: 8,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Extended-wear clinical · lab · industrial",
    features: [
      "FDA 510(k) exam-grade",
      "Heavy 8 mil thickness",
      "Textured, ambidextrous",
      "Powder-free",
    ],
    standards: ["FDA 510(k)", "ASTM D3578"],
    image: "/api/static/gloves/real/TLF-ivory-latex.png",
    productUrl: "https://www.gloveworks.com/product/gplhd-heavy-duty-blue-latex-exam",
    specSheet: "https://www.gloveworks.com/sites/default/files/GPLHD-spec.pdf",
    retailPerCase: 180.72,
    retailPerPiece: 0.1807,
    variants: [
      { size: "XL", sku: "GPLHD84100" },
      { size: "XXL", sku: "GPLHD86100" },
      { size: "XXXL", sku: "GPLHD88100" },
    ],
  },
  {
    id: "gloveworks-ivory-latex-ind",
    name: "Gloveworks Ivory Latex PF Industrial Gloves",
    brand: "Gloveworks",
    material: "latex",
    color: "Ivory",
    mil: 4,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Everyday utility · light industrial",
    features: ["Textured grip", "4 mil industrial", "Powder-free"],
    standards: ["ASTM D3578"],
    image: "/api/static/gloves/real/TLF-ivory-latex.png",
    productUrl: "https://www.gloveworks.com/product/tlf-ivory-latex-industrial",
    specSheet: "https://www.gloveworks.com/sites/default/files/TLF-spec.pdf",
    retailPerCase: 79.20,
    retailPerPiece: 0.0792,
    variants: EXAM_SIZES_S_XL("TLF"),
  },
  {
    id: "gloveworks-ivory-latex-ind-hd",
    name: "Gloveworks Heavy-Duty Ivory Latex Industrial Gloves",
    brand: "Gloveworks",
    material: "latex",
    color: "Ivory",
    mil: 8,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Extended-durability industrial · heavy trades",
    features: ["Heavy-duty 8 mil textured", "Extended durability", "Powder-free"],
    standards: ["ASTM D3578"],
    image: "/api/static/gloves/real/TLF-ivory-latex.png",
    productUrl: "https://www.gloveworks.com/product/ilhd-heavy-duty-ivory-latex",
    specSheet: "https://www.gloveworks.com/sites/default/files/ILHD-spec.pdf",
    retailPerCase: 145.44,
    retailPerPiece: 0.1454,
    variants: EXAM_SIZES_S_XL("ILHD"),
  },
  {
    id: "gloveworks-poly-textured-ind",
    name: "Gloveworks Poly Textured Industrial Gloves",
    brand: "Gloveworks",
    material: "poly",
    color: "Clear",
    mil: 3.9,
    packDescription: "1,000 gloves / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Food service · light utility",
    features: [
      "Embossed polyurethane texture",
      "Ambidextrous",
      "Latex-free · powder-free",
    ],
    standards: ["FDA food-contact"],
    image: "/api/static/gloves/real/PGLOVE500-clear-poly.png",
    productUrl: "https://www.gloveworks.com/product/gppft-poly-textured-industrial",
    specSheet: "https://www.gloveworks.com/sites/default/files/GPPFT-poly-spec.pdf",
    retailPerCase: 92.88,
    retailPerPiece: 0.0929,
    variants: EXAM_SIZES_S_XL("GPPFT"),
  },
  {
    id: "gloveworks-clear-vinyl-ind",
    name: "Gloveworks Clear Vinyl PF Industrial Gloves",
    brand: "Gloveworks",
    material: "vinyl",
    color: "Clear",
    mil: 3,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Food handling · cleaning · light-touch",
    features: [
      "Powder-free · latex-free",
      "Ambidextrous",
      "Cost-effective disposable",
    ],
    standards: ["FDA food-contact"],
    image: "/api/static/gloves/real/VPF-clear-vinyl-exam.png",
    productUrl: "https://www.gloveworks.com/product/ivpf-clear-vinyl-industrial",
    specSheet: "https://www.gloveworks.com/sites/default/files/IVPF-spec.pdf",
    retailPerCase: 46.08,
    retailPerPiece: 0.0461,
    variants: EXAM_SIZES_S_XL("IVPF"),
  },
  {
    id: "gloveworks-blue-vinyl-ind",
    name: "Gloveworks Blue Vinyl PF Industrial Gloves",
    brand: "Gloveworks",
    material: "vinyl",
    color: "Blue",
    mil: 3,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Food-safe general purpose · janitorial",
    features: [
      "Powder-free · latex-free",
      "Food-safe blue color-coding",
      "Ambidextrous",
    ],
    standards: ["FDA food-contact"],
    image: "/api/static/gloves/real/IVBPF-blue-vinyl.png",
    productUrl: "https://www.gloveworks.com/product/ivbpf-blue-vinyl-industrial",
    specSheet: "https://www.gloveworks.com/sites/default/files/IVBPF-spec.pdf",
    retailPerCase: 46.08,
    retailPerPiece: 0.0461,
    variants: EXAM_SIZES_S_XL("IVBPF"),
  },

  // =========================================================================
  // GLOVEWORKS RDT — RAISED DIAMOND TEXTURE (heaviest industrial)
  // =========================================================================
  {
    id: "gloveworks-rdt-black-nitrile",
    name: "Gloveworks RDT Black Nitrile PF Industrial Gloves",
    brand: "Gloveworks",
    material: "nitrile",
    color: "Black",
    mil: 8,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Automotive · heavy industrial · mechanical trades",
    features: [
      "Raised Diamond Texture (RDT) grip",
      "Heavy 8 mil thickness",
      "Superior wet/dry grip",
      "Latex-free · powder-free",
    ],
    standards: ["ASTM D6319", "EN 374"],
    image: "/api/static/gloves/real/GPNB-black-nitrile.png",
    productUrl: "https://www.gloveworks.com/product/gwbn-rdt-black-nitrile",
    specSheet: "https://www.gloveworks.com/sites/default/files/GWBN-spec.pdf",
    retailPerCase: 201.60,
    retailPerPiece: 0.2016,
    variants: [
      { size: "M", sku: "GWBN44100" },
      { size: "L", sku: "GWBN46100" },
      { size: "XL", sku: "GWBN48100" },
      { size: "XXL", sku: "GWBN49100" },
    ],
  },
  {
    id: "gloveworks-rdt-green-nitrile",
    name: "Gloveworks RDT Green Nitrile PF Industrial Gloves",
    brand: "Gloveworks",
    material: "nitrile",
    color: "Green",
    mil: 8,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "High-visibility industrial · food processing color-coding",
    features: [
      "Raised Diamond Texture (RDT)",
      "8 mil heavy-duty",
      "High-visibility green",
      "Latex-free · powder-free",
    ],
    standards: ["ASTM D6319"],
    image: "/api/static/gloves/real/GWGN-green-nitrile.png",
    productUrl: "https://www.gloveworks.com/product/gwgn-rdt-green-nitrile",
    specSheet: "https://www.gloveworks.com/sites/default/files/GWGN-spec.pdf",
    retailPerCase: 224.64,
    retailPerPiece: 0.2246,
    variants: [
      { size: "S", sku: "GWGN42100" },
      { size: "M", sku: "GWGN44100" },
      { size: "L", sku: "GWGN46100" },
      { size: "XL", sku: "GWGN48100" },
      { size: "XXL", sku: "GWGN49100" },
    ],
  },
  {
    id: "gloveworks-rdt-orange-nitrile",
    name: "Gloveworks RDT Orange Nitrile PF Industrial Gloves",
    brand: "Gloveworks",
    material: "nitrile",
    color: "Orange",
    mil: 8,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Outdoor · mechanical · high-vis safety trades",
    features: [
      "Raised Diamond Texture (RDT)",
      "8 mil heavy-duty",
      "High-visibility orange",
      "Latex-free · powder-free",
    ],
    standards: ["ASTM D6319"],
    image: "/api/static/gloves/real/GWON-orange-nitrile.png",
    productUrl: "https://www.gloveworks.com/product/gwon-rdt-orange-nitrile",
    specSheet: "https://www.gloveworks.com/sites/default/files/GWON-spec.pdf",
    retailPerCase: 224.64,
    retailPerPiece: 0.2246,
    variants: [
      { size: "S", sku: "GWON42100" },
      { size: "M", sku: "GWON44100" },
      { size: "L", sku: "GWON46100" },
      { size: "XL", sku: "GWON48100" },
      { size: "XXL", sku: "GWON49100" },
    ],
  },
  {
    id: "gloveworks-rdt-royal-blue-nitrile",
    name: "Gloveworks RDT Royal Blue Nitrile PF Industrial Gloves",
    brand: "Gloveworks",
    material: "nitrile",
    color: "Royal Blue",
    mil: 8,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Heavy industrial · janitorial · plumbing",
    features: [
      "Raised Diamond Texture (RDT)",
      "8 mil heavy-duty",
      "Superior grip",
      "Latex-free · powder-free",
    ],
    standards: ["ASTM D6319"],
    image: "/api/static/gloves/real/GWRBN-royal-blue-nitrile.png",
    productUrl: "https://www.gloveworks.com/product/gwrbn-rdt-royal-blue-nitrile",
    specSheet: "https://www.gloveworks.com/sites/default/files/GWRBN-spec.pdf",
    retailPerCase: 201.60,
    retailPerPiece: 0.2016,
    variants: [
      { size: "M", sku: "GWRBN44100" },
      { size: "L", sku: "GWRBN46100" },
      { size: "XL", sku: "GWRBN48100" },
      { size: "XXL", sku: "GWRBN49100" },
    ],
  },

  // =========================================================================
  // GLOVEWORKS ULTRA — Flock-lined
  // =========================================================================
  {
    id: "gloveworks-ultra-orange-flock",
    name: "Gloveworks ULTRA Orange Flock-Lined Nitrile Gloves",
    brand: "Gloveworks ULTRA",
    material: "nitrile",
    color: "Orange",
    mil: 8,
    packDescription: "50 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 500,
    useCase: "Automotive · industrial · long-wear (keep hands cool & dry)",
    features: [
      "Moisture-absorbing flocked lining",
      "Raised Diamond Texture grip",
      "Heavy 8 mil thickness",
      "Extended-wear comfort",
    ],
    standards: ["ASTM D6319", "EN 374"],
    image: "/api/static/gloves/real/GWON-orange-nitrile.png",
    productUrl: "https://www.gloveworks.com/product/gwuofn-ultra-flock-lined-orange",
    specSheet: "https://www.gloveworks.com/sites/default/files/GWUOFN-spec.pdf",
    retailPerCase: 166.32,
    retailPerPiece: 0.3326,
    variants: [
      { size: "M", sku: "GWUOFN44050" },
      { size: "L", sku: "GWUOFN46050" },
      { size: "XL", sku: "GWUOFN48050" },
      { size: "XXL", sku: "GWUOFN49050" },
    ],
  },

  // =========================================================================
  // X3 — LIGHT INDUSTRIAL (economy tier)
  // =========================================================================
  {
    id: "x3-blue-nitrile-ind",
    name: "X3 Blue Nitrile PF Industrial Gloves",
    brand: "X3",
    material: "nitrile",
    color: "Blue",
    mil: 3.4,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Economical daily-use disposable · janitorial",
    features: [
      "Light 3.4 mil industrial",
      "Textured fingertips",
      "Latex-free · powder-free",
      "Value case pricing",
    ],
    standards: ["ASTM D6319"],
    image: "/api/static/gloves/real/X3-blue-nitrile.png",
    productUrl: "https://www.x3gloves.com/product/x3-blue-nitrile",
    specSheet: "https://www.x3gloves.com/sites/default/files/X3-spec.pdf",
    retailPerCase: 80.64,
    retailPerPiece: 0.0806,
    variants: [
      { size: "S", sku: "X342100" },
      { size: "M", sku: "X344100" },
      { size: "L", sku: "X346100" },
      { size: "XL", sku: "X348100" },
      { size: "XXL", sku: "X349100" },
    ],
  },
  {
    id: "x3-black-nitrile-ind",
    name: "X3 Black Nitrile PF Industrial Gloves",
    brand: "X3",
    material: "nitrile",
    color: "Black",
    mil: 3.4,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Automotive · janitorial · everyday economy",
    features: [
      "Light 3.4 mil industrial",
      "Textured fingertips",
      "Latex-free · powder-free",
    ],
    standards: ["ASTM D6319"],
    image: "/api/static/gloves/real/GPNB-black-nitrile.png",
    productUrl: "https://www.x3gloves.com/product/x3-black-nitrile",
    specSheet: "https://www.x3gloves.com/sites/default/files/BX3-spec.pdf",
    retailPerCase: 80.64,
    retailPerPiece: 0.0806,
    variants: [
      { size: "M", sku: "BX342100" },
      { size: "L", sku: "BX344100" },
      { size: "XL", sku: "BX346100" },
      { size: "XXL", sku: "BX348100" },
      { size: "XXXL", sku: "BX349100" },
    ],
  },
  {
    id: "x3-black-nitrile-hd",
    name: "X3 Heavy-Duty Black Nitrile Industrial Gloves",
    brand: "X3",
    material: "nitrile",
    color: "Black",
    mil: 5,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Automotive · heavy industrial value-tier",
    features: ["Heavier 5 mil", "Textured for grip", "Latex-free · powder-free"],
    standards: ["ASTM D6319"],
    image: "/api/static/gloves/real/GPNB-black-nitrile.png",
    productUrl: "https://www.x3gloves.com/product/xbn-heavy-duty-black-nitrile",
    specSheet: "https://www.x3gloves.com/sites/default/files/XBN-spec.pdf",
    retailPerCase: 101.52,
    retailPerPiece: 0.1015,
    variants: [
      { size: "S", sku: "XBN42100" },
      { size: "M", sku: "XBN44100" },
      { size: "L", sku: "XBN46100" },
      { size: "XL", sku: "XBN48100" },
      { size: "XXL", sku: "XBN49100" },
    ],
  },
  {
    id: "x3-ivory-latex-ind",
    name: "X3 Ivory Latex PF Industrial Gloves",
    brand: "X3",
    material: "latex",
    color: "Ivory",
    mil: 4,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Everyday utility · light industrial",
    features: ["4 mil textured", "Powder-free", "Cost-effective bulk value"],
    standards: ["ASTM D3578"],
    image: "/api/static/gloves/real/TLF-ivory-latex.png",
    productUrl: "https://www.x3gloves.com/product/lx3-ivory-latex",
    specSheet: "https://www.x3gloves.com/sites/default/files/LX3-spec.pdf",
    retailPerCase: 75.60,
    retailPerPiece: 0.0756,
    variants: EXAM_SIZES_S_XL("LX3"),
  },
  {
    id: "x3-clear-vinyl-ind",
    name: "X3 Clear Vinyl PF Industrial Gloves",
    brand: "X3",
    material: "vinyl",
    color: "Clear",
    mil: 3,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Food handling · cleaning · low-cost daily",
    features: ["3 mil industrial", "Powder-free · latex-free", "Food-safe"],
    standards: ["FDA food-contact"],
    image: "/api/static/gloves/real/VPF-clear-vinyl-exam.png",
    productUrl: "https://www.x3gloves.com/product/gpx3-clear-vinyl",
    specSheet: "https://www.x3gloves.com/sites/default/files/GPX3-spec.pdf",
    retailPerCase: 43.92,
    retailPerPiece: 0.0439,
    variants: [
      { size: "S", sku: "GPX342100" },
      { size: "M", sku: "GPX344100" },
      { size: "L", sku: "GPX346100" },
      { size: "XL", sku: "GPX348100" },
    ],
  },
  {
    id: "vpf-clear-vinyl-exam",
    name: "VPF Clear Vinyl PF Exam Gloves",
    brand: "AMMEX",
    material: "vinyl",
    color: "Clear",
    mil: 4,
    packDescription: "100 gloves / box · 10 boxes / case",
    uom: "CA",
    caseCount: 1000,
    useCase: "Medical exam · short-duration food service",
    features: [
      "FDA 510(k) exam-grade",
      "4 mil powder-free · latex-free",
      "Case-quantity value pricing",
    ],
    standards: ["FDA 510(k)", "ASTM D5250"],
    image: "/api/static/gloves/real/VPF-clear-vinyl-exam.png",
    productUrl: "https://www.ammexprofessional.com/product/vpf-clear-vinyl-exam",
    specSheet: "https://www.ammexprofessional.com/sites/default/files/VPF-spec.pdf",
    retailPerCase: 51.12,
    retailPerPiece: 0.0511,
    variants: [
      { size: "S", sku: "VPF62100" },
      { size: "M", sku: "VPF64100" },
      { size: "L", sku: "VPF66100" },
      { size: "XL", sku: "VPF68100" },
    ],
  },
  {
    id: "poly-food-service",
    name: "Poly Food-Service Gloves — 500 Count Dispenser",
    brand: "AMMEX",
    material: "poly",
    color: "Clear",
    mil: 1,
    packDescription: "500 gloves / dispenser box",
    uom: "CA",
    caseCount: 500,
    useCase: "Food service · cafeteria · quick-service disposable",
    features: [
      "Loose-fit ambidextrous",
      "Powder-free · latex-free",
      "500-count dispenser",
    ],
    standards: ["FDA food-contact"],
    image: "/api/static/gloves/real/PGLOVE500-clear-poly.png",
    productUrl: "https://www.ammex.com/product/pglove-poly-food-service",
    specSheet: "https://www.ammex.com/sites/default/files/PGLOVE-spec.pdf",
    retailPerCase: 92.88,
    retailPerPiece: 0.1858,
    variants: [
      { size: "M", sku: "PGLOVE-M-500" },
      { size: "L", sku: "PGLOVE-L-500" },
    ],
  },
];

export const GLOVES_COUNT = GLOVES.length;
export const GLOVES_SKU_COUNT = GLOVES.reduce((n, g) => n + g.variants.length, 0);
